// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useEffect } from "react";

interface UseEscapeToClearFiltersOptions {
  /** Whether any filter is currently active — Escape only clears when something is set. */
  isFiltered: boolean;
  /** Resets every filter to its default. */
  clearFilters: () => void;
  /**
   * When true, Escape is ignored so an open dialog/popover can handle the key itself. Pass the
   * OR of the page's dialog-open flags (e.g. `createDialogOpen || toggleTarget !== null`).
   */
  disabled?: boolean;
}

/**
 * Clear all list filters when Escape is pressed — a single document-level handler shared by every
 * data-table list page so the behavior stays consistent. Ignores the key when no filter is active,
 * when another handler already consumed it (`defaultPrevented`), or when `disabled`.
 */
export function useEscapeToClearFilters({
  isFiltered,
  clearFilters,
  disabled = false,
}: UseEscapeToClearFiltersOptions) {
  useEffect(
    function registerEscapeToClearFilters() {
      function onKeyDown(e: KeyboardEvent) {
        if (e.key !== "Escape" || e.defaultPrevented) return;
        if (!isFiltered || disabled) return;
        clearFilters();
      }
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    },
    [isFiltered, disabled, clearFilters],
  );
}
